import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import cn from 'classnames';
import styles from './ScrollableList.module.scss';
/**
 * A scrollable container component that renders a list of items.
 * Supports both vertical and horizontal scrolling directions.
 */ export const ScrollableList = /*#__PURE__*/ React.forwardRef(function ScrollableList(props, ref) {
    const { children, className, direction = 'horizontal', maxSize, minSize, hideScrollbar = false, gap, itemMinSize, itemMaxSize, itemsInView, style } = props, rest = _object_without_properties(props, [
        "children",
        "className",
        "direction",
        "maxSize",
        "minSize",
        "hideScrollbar",
        "gap",
        "itemMinSize",
        "itemMaxSize",
        "itemsInView",
        "style"
    ]);
    const computedStyle = useMemo(()=>{
        const styleObj = _object_spread({}, style);
        const formatToPx = (size)=>"".concat(size, "px");
        const isPxValue = (size)=>{
            if (!size) return false;
            return typeof size === 'number' || size.toString().endsWith('px');
        };
        const parseSize = (size)=>{
            if (!size) return 0;
            return typeof size === 'number' ? size : parseFloat(size) || 0;
        };
        const sizeProperty = direction === 'vertical' ? 'height' : 'width';
        const maxSizeProperty = direction === 'vertical' ? 'maxHeight' : 'maxWidth';
        const minSizeProperty = direction === 'vertical' ? 'minHeight' : 'minWidth';
        // Set container min/max size constraints
        if (maxSize) {
            styleObj[maxSizeProperty] = maxSize;
        }
        if (minSize) {
            styleObj[minSizeProperty] = minSize;
        }
        // Set item min/max size constraints
        if (itemMinSize !== undefined) {
            styleObj['--item-min-size'] = formatToPx(itemMinSize);
        }
        if (itemMaxSize !== undefined) {
            styleObj['--item-max-size'] = formatToPx(itemMaxSize);
        }
        // Set gap
        if (gap) {
            styleObj.gap = formatToPx(gap);
        }
        // Calculate item size based on itemsInView if provided
        if (itemsInView) {
            const gapsCount = Math.ceil(itemsInView) - 1;
            const gapsTotal = Math.max(0, gapsCount) * (gap !== null && gap !== void 0 ? gap : 0);
            if (maxSize && isPxValue(maxSize)) {
                // Case: maxSize is provided
                const containerMinSize = isPxValue(minSize) ? parseSize(minSize) : 0;
                const containerMaxSize = Math.max(containerMinSize, parseSize(maxSize));
                const availableSpace = containerMaxSize - gapsTotal;
                const requiredItemSize = availableSpace / itemsInView;
                // Clamp to item constraints
                let finalItemSize = Math.floor(requiredItemSize);
                if (itemMinSize && finalItemSize < itemMinSize) {
                    finalItemSize = itemMinSize;
                }
                if (itemMaxSize && finalItemSize > itemMaxSize) {
                    finalItemSize = itemMaxSize;
                }
                styleObj[sizeProperty] = 'fit-content';
                styleObj['--item-size'] = formatToPx(finalItemSize);
            } else {
                if (itemMaxSize && itemMaxSize > 0) {
                    // Case: maxItemSize is provided (no maxSize)
                    const calculatedContainerSize = itemMaxSize * itemsInView + gapsTotal;
                    styleObj[sizeProperty] = formatToPx(calculatedContainerSize);
                    styleObj['--item-size'] = formatToPx(itemMaxSize);
                } else {
                    // Case: no maxSize and no maxItemSize - use percentage
                    const itemSizePercent = gapsTotal > 0 ? "calc((100% - ".concat(gapsTotal, "px) / ").concat(itemsInView, ")") : "calc(100% / ".concat(itemsInView, ")");
                    styleObj['--item-size'] = itemSizePercent;
                }
            }
        }
        return styleObj;
    }, [
        direction,
        maxSize,
        minSize,
        gap,
        itemMinSize,
        itemMaxSize,
        itemsInView,
        style
    ]);
    return /*#__PURE__*/ _jsx("div", _object_spread_props(_object_spread({
        ref: ref,
        className: cn(styles.root, styles[direction], {
            [styles.hideScrollbar]: hideScrollbar
        }, className),
        style: computedStyle
    }, rest), {
        children: children
    }));
});
